import * as React from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import ChecklistIcon from '@material-ui/icons/PlaylistAddCheck';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { getPendingInspectionsCount, getPendingReviewsCount } from '../api/inspections';
import { ROUTES } from '../constants/routes';

const InspectionTasksBlock = () => {
  const history = useHistory();
  const [pendingInspections, setPendingInspections] = React.useState<number | null>(null);
  const [pendingReviews, setPendingReviews] = React.useState<number | null>(null);

  React.useEffect(() => {
    Promise.all([
      getPendingInspectionsCount(),
      getPendingReviewsCount(),
    ]).then(([insp, rev]) => {
      setPendingInspections(insp.data);
      setPendingReviews(rev.data);
    });
  }, []);

  const total = (pendingInspections ?? 0) + (pendingReviews ?? 0);
  const fmt = (n: number | null) => (n === null ? '—' : n);

  return (
    <Wrapper>
      <StatRow
        type="button"
        onClick={() => history.push(ROUTES.INSPECTIONS)}
      >
        <IconSquare $bg="#E9F2FF" $fg="#2563EB">
          <AssignmentTurnedInIcon />
        </IconSquare>
        <Body>
          <Label>Pending Inspections</Label>
          <Value>{fmt(pendingInspections)}</Value>
        </Body>
        <Chevron>
          <ArrowForwardIcon style={{ fontSize: 16 }} />
        </Chevron>
      </StatRow>

      <StatRow
        type="button"
        onClick={() => history.push(ROUTES.REVIEWS)}
      >
        <IconSquare $bg="#FFF1D6" $fg="#B7800A">
          <ChecklistIcon />
        </IconSquare>
        <Body>
          <Label>Pending Reviews</Label>
          <Value>{fmt(pendingReviews)}</Value>
        </Body>
        <Chevron>
          <ArrowForwardIcon style={{ fontSize: 16 }} />
        </Chevron>
      </StatRow>

      <TotalRow>
        <IconSquare $bg="#DCFCE7" $fg="#16A34A">
          <TrendingUpIcon />
        </IconSquare>
        <Body>
          <Label>Total</Label>
          <Value>{pendingInspections === null && pendingReviews === null ? '—' : total}</Value>
        </Body>
      </TotalRow>
    </Wrapper>
  );
};

export default InspectionTasksBlock;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const RowBase = `
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-sizing: border-box;
  font-family: inherit;
  text-align: left;
`;

const StatRow = styled.button`
  ${RowBase}
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;

  &:hover {
    border-color: var(--neutral-300);
    box-shadow: var(--shadow-xs);
  }

  &:active {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const TotalRow = styled.div`
  ${RowBase}
  background: var(--neutral-50);
`;

const IconSquare = styled.div<{ $bg: string; $fg: string }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 20px;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Label = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  line-height: 1.2;
`;

const Value = styled.div`
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.15;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
`;

const Chevron = styled.span`
  color: var(--neutral-400);
  display: inline-flex;
  align-items: center;
  transition: color 0.15s ease, transform 0.15s ease;

  ${StatRow}:hover & {
    color: var(--primary-600);
    transform: translateX(2px);
  }
`;
